import { ref, onMounted } from "vue";
import type { IComposableInterface } from "./types";
import { useCommon } from "./use-common";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useDirectBackend } from "#imports";

export const useExistingAddress = (): IComposableInterface => {
  const business = useBusinessStore();
  const basket = useBasketStore();
  const customer = useCustomerStore();

  const { customerRepo } = useDirectBackend();

  const {
    formErrors,
    isLoading,
    isSaving,
    record,
    setFieldValue,
    getNicknameLabel,
    noop,
    handleSaveError,
  } = useCommon();

  const recordId = ref("");

  const saveAddress = async (onSuccess: () => void = noop) => {
    formErrors.value = {};
    isSaving.value = true;

    try {
      await customerRepo.updateAddress({
        addressId: recordId.value,
        nickname: getNicknameLabel(record.value.nickname),
        coordinate: {
          lat: record.value.lat,
          lng: record.value.lng,
        },
        city: record.value.city ?? "",
        number: record.value.number,
        location: record.value.location ?? "",
        zipCode: record.value.zipCode,
        instruction: record.value.instruction,
        country: business.country,
      });

      await customer.syncAddresses(basket.businessId);

      onSuccess();
    } catch (e) {
      handleSaveError(e);
    } finally {
      isSaving.value = false;
    }
  };

  const loadAddress = () => {
    const address = customer.addresses.find(
      (item) => item.id === basket.addressId,
    );

    if (!address) {
      return;
    }

    recordId.value = address.id;
    record.value = {
      location: address.location,
      number: address.number,
      instruction: address.instruction,
      zipCode: address.zipCode,
      city: address.city ?? "",
      lat: address.lat,
      lng: address.lng,
      nickname: "other",
    };
  };

  onMounted(() => {
    loadAddress();
    isLoading.value = false;
  });

  return {
    isLoading,
    isSaving,
    record,
    formErrors,
    setFieldValue,
    saveAddress,
  };
};
